/**
 * The base id grammar, and the two duties it serves.
 *
 * A leaf module (no imports) because both consumers are load-bearing and one of
 * them is a security check:
 *
 *  - **The worktree binding** (`base-binding.ts`) needs it strict enough that a
 *    garbled or multi-line file reads as unbound, so the routing tripwire fails
 *    open to "no binding" rather than wedging on corrupt state.
 *  - **URL path construction** (`data/helpers.ts`) needs it strict enough that an
 *    id cannot carry a path separator or a dot segment into a request path.
 *
 * They are stated together because the second is easy to weaken by accident
 * while satisfying the first: loosening the grammar "so a binding file is more
 * forgiving" would silently loosen what may become a URL segment. Any change
 * here has to be justified against BOTH.
 */

// A base id is a slug (`my-base`, `prod--my-preview`), never a UUID. No
// whitespace, no path separators, bounded length. The leading `(?!\.\.?$)`
// rejects `.` and `..`: the character class already blocks `/`, but a bare `..`
// is still a traversal segment — `path.join` would resolve it to the parent
// directory, and `fetch()`'s URL parser resolves it in a request path.
const BASE_ID_RE = /^(?!\.\.?$)[A-Za-z0-9._-]{1,256}$/;

/** True when `id` is a structurally valid base id. */
export function isValidBaseId(id: string): boolean {
  return BASE_ID_RE.test(id);
}
